import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownToLine, ArrowLeft, ArrowRight, ArrowUpRight, Check, Copy, Github } from 'lucide-react';
import { siLua, siNodedotjs, siPython } from 'simple-icons';
import './sdks.css';

const repository = 'https://github.com/bcktio/bckt-cfx';
const version = '0.1.4';
const download = `${repository}/releases/download/v${version}/bckt-cfx-${version}.zip`;
const installation = 'set bckt_api_key "bckt_your_api_key"\nensure bckt';
const cfxExamples = [
  {
    id: 'upload', label: 'Upload a file', filename: 'server.lua',
    code: `CreateThread(function()
    local result = exports['bckt']:UploadJsonAwait({
        filename = 'server-state.json',
        folder = 'backups',
        private = true,
        data = { players = #GetPlayers() }
    })

    if not result.success then
        print(result.error.code, result.error.message)
    end
end)`,
    note: 'Upload JSON, raw content or files from a resource. Keep them private or make them public.',
    link: `${repository}/blob/main/docs/exports.md`, linkLabel: 'Explore file exports',
  },
  {
    id: 'logs', label: 'Send a log', filename: 'server.lua',
    code: `exports['bckt']:Log('server', {
    event_type = 'resource.started',
    entity_id = GetCurrentResourceName(),
    message = 'Resource is ready',
    payload = {
        players = #GetPlayers()
    }
})`,
    note: 'Create the server log stream first. Log queues the event; SendLogsAwait waits for API acceptance.',
    link: `${repository}/blob/main/docs/operations.md`, linkLabel: 'Read about log delivery',
  },
  {
    id: 'phone', label: 'Phone uploads', filename: 'server.lua',
    code: `local result = exports['bckt']:CreateUploadUrlAwait({
    filename = 'photo.webp',
    content_type = 'image/webp',
    size = imageSize,
    folder = 'phone/photos',
    private = false
})

if result.success then
    TriggerClientEvent('phone:upload', playerId, result.data)
end`,
    note: 'Inside your authorized upload handler: validate the player, image type and size first. Pass the ticket to the client, never the API key.',
    link: `${repository}/tree/main/examples/phone-upload`, linkLabel: 'Get the complete phone example',
  },
  {
    id: 'capture', label: 'Screenshots', filename: 'server.lua',
    code: `local result = exports['bckt']:CaptureScreenshotAwait(playerId, {
    folder = 'reports',
    private = true,
    encoding = 'webp',
    quality = 0.85
})

if result.success then
    print(result.data.file.id, result.data.url)
else
    print(result.error.code, result.error.message)
end`,
    note: 'Run from a yieldable server thread with an authorized playerId. Supports screenshot-basic or screencapture. Select either with the provider option; RedM compatibility depends on the capture resource.',
    link: `${repository}#screenshots-without-writing-the-plumbing`, linkLabel: 'Screenshot setup & requirements',
  },
  {
    id: 'video', label: 'Video clips', filename: 'server.lua',
    code: `local result = exports['bckt']:CaptureVideoAwait(playerId, {
    duration = 15,
    maxWidth = 1280,
    maxHeight = 720,
    folder = 'reports/clips',
    private = true
})

if result.success then
    print('Video saved:', result.data.file.id)
else
    print(result.error.code, result.error.message)
end`,
    note: 'Requires screencapture video exports and both file scopes. Records WebM, uploads to BCKT and returns a verified file with an access URL. Video support is experimental and needs live game testing.',
    link: `${repository}/tree/main/examples/video`, linkLabel: 'Video controls & setup',
  },
] as const;

const nodeRepository = 'https://github.com/bcktio/bckt-node';
const nodeVersion = '0.1.0-beta.1';
const npmInstall = 'npm install @bckt/sdk@beta';
const nodeClient = `import { Bckt } from '@bckt/sdk';

const bckt = new Bckt({ apiKey: process.env.BCKT_API_KEY });`;
const nodeExamples = [
  {
    id: 'upload', label: 'Upload a file', filename: 'app.mjs',
    code: `${nodeClient}

const uploaded = await bckt.files.uploadFile('./photo.webp', {
  folder: 'phone/photos',
  private: false,
});

console.log(uploaded.url);`,
    note: 'Stream a file from disk, or upload a Buffer, Blob or readable stream. The API key stays on your server.',
    link: `${nodeRepository}/blob/main/docs/reference.md#files`, linkLabel: 'Explore file methods',
  },
  {
    id: 'logs', label: 'Send a log', filename: 'app.mjs',
    code: `${nodeClient}

await bckt.logs.send('payments', {
  level: 'info',
  eventType: 'payment.completed',
  entityId: 'order_123',
  message: 'Payment received',
  payload: { amount: 29.99, currency: 'EUR' },
});`,
    note: 'Create the payments stream first. Send a single event, submit a batch or use the optional buffered logger.',
    link: `${nodeRepository}/blob/main/docs/reference.md#logs`, linkLabel: 'Explore log methods',
  },
  {
    id: 'browser', label: 'Browser uploads', filename: 'server.mjs',
    code: `${nodeClient}

const ticket = await bckt.files.createUploadUrl({
  filename: 'photo.webp',
  contentType: 'image/webp',
  size: imageSize,
  folder: 'phone/photos',
  private: false,
});`,
    note: 'Inside your authorized handler, validate the user and imageSize first. Send the ticket to the browser, which uploads the raw file directly.',
    link: `${nodeRepository}/tree/main/examples/browser-upload`, linkLabel: 'Get the browser upload example',
  },
  {
    id: 's3', label: 'S3 URLs', filename: 'app.mjs',
    code: `${nodeClient}

const signed = await bckt.s3.createPresignedUrl({
  key: 'backups/report.enc',
  method: 'GET',
  expiresIn: 300,
});`,
    note: 'Create a temporary URL for an S3 operation. Requires active S3 credentials in your BCKT workspace.',
    link: `${nodeRepository}/blob/main/examples/s3.md`, linkLabel: 'Read the S3 example',
  },
] as const;

const pythonRepository = 'https://github.com/bcktio/bckt-python';
const pythonVersion = '0.1.0b1';
const pythonPackage = `https://pypi.org/project/bckt-sdk/${pythonVersion}/`;
const pipInstall = 'python -m pip install --pre bckt-sdk';
const pythonClient = `import os
from bckt import Bckt`;
const pythonExamples = [
  {
    id: 'upload', label: 'Upload a file', filename: 'app.py',
    code: `${pythonClient}

with Bckt(os.environ["BCKT_API_KEY"]) as bckt:
    uploaded = bckt.files.upload_file(
        "./photo.webp",
        folder="photos",
        private=False,
    )
    print(uploaded["url"])`,
    note: 'Stream files from disk, upload bytes or send JSON. The context manager closes the client when you finish.',
    link: `${pythonRepository}/blob/main/docs/reference.md#files`, linkLabel: 'Explore file methods',
  },
  {
    id: 'logs', label: 'Send a log', filename: 'app.py',
    code: `${pythonClient}

with Bckt(os.environ["BCKT_API_KEY"]) as bckt:
    bckt.logs.send("payments", {
        "event_type": "payment.completed",
        "entity_id": "order_123",
        "message": "Payment received",
        "payload": {"amount": 29.99, "currency": "EUR"},
    })`,
    note: 'Create the payments stream first. Send events, submit batches or use a bounded logger with explicit flushing.',
    link: `${pythonRepository}/blob/main/docs/reference.md#logs`, linkLabel: 'Explore log methods',
  },
  {
    id: 'browser', label: 'Browser uploads', filename: 'server.py',
    code: `${pythonClient}

with Bckt(os.environ["BCKT_API_KEY"]) as bckt:
    ticket = bckt.files.create_upload_url(
        filename="photo.webp",
        size=image_size,
        content_type="image/webp",
        folder="phone/photos",
        private=False,
    )`,
    note: 'Validate the user, image type and image_size in your server handler. Return the ticket to the browser so it can upload the raw file directly.',
    link: `${pythonRepository}/tree/main/examples/browser-upload`, linkLabel: 'Get the browser upload example',
  },
  {
    id: 'async', label: 'Async uploads', filename: 'app.py',
    code: `import asyncio
import os
from bckt import AsyncBckt

async def main():
    async with AsyncBckt(os.environ["BCKT_API_KEY"]) as bckt:
        uploaded = await bckt.files.upload_file(
            "./photo.webp", folder="photos", private=True,
        )
        print(uploaded["file_key"])

asyncio.run(main())`,
    note: 'Use native async requests with asyncio. Keep one client open across operations and your API key on the server.',
    link: `${pythonRepository}/blob/main/examples/async_upload.py`, linkLabel: 'Open the async example',
  },
] as const;

const sdkCatalog = {
  cfx: {
    title: 'FiveM & RedM', language: 'Lua SDK', icon: siLua, version,
    description: 'The photo from your phone. The screenshot for a report. The log you need tomorrow. All through your own resources.',
    capabilities: ['Server exports & direct client uploads', 'Screenshots & screencapture video clips', 'No ESX or QBCore dependency'],
    repository, download, extension: '.zip', guide: '/sdks/fivem-redm',
    reference: `${repository}/blob/main/docs/exports.md`, referenceLabel: 'Export reference', examples: cfxExamples,
  },
  node: {
    title: 'Node.js', language: 'JavaScript & TypeScript SDK', icon: siNodedotjs, version: nodeVersion,
    description: 'Upload from your backend. Let the browser send the photo. Keep the logs that explain what happened.',
    capabilities: ['Streaming uploads & temporary upload URLs', 'Structured logs & a buffered logger', 'TypeScript, ESM & CommonJS. No runtime dependencies.'],
    repository: nodeRepository, download: 'https://registry.npmjs.org/@bckt/sdk/-/sdk-0.1.0-beta.1.tgz', extension: '.tgz', guide: '/sdks/nodejs',
    reference: `${nodeRepository}/blob/main/docs/reference.md`, referenceLabel: 'Method reference', examples: nodeExamples,
  },
  python: {
    title: 'Python', language: 'Python SDK', icon: siPython, version: pythonVersion,
    description: 'A script, a worker or your next backend. Upload files, search your logs and keep your application moving.',
    capabilities: ['Sync & async clients for Python 3.10+', 'Streaming uploads & direct browser uploads', 'Structured logs, bounded queues & S3 URLs'],
    repository: pythonRepository,
    download: 'https://files.pythonhosted.org/packages/5a/27/cae4135a272cbf3148a84a9249d0d2a5075cee50178fd1eb9f84e49efe4e/bckt_sdk-0.1.0b1-py3-none-any.whl',
    extension: '.whl', guide: '/sdks/python',
    reference: `${pythonRepository}/blob/main/docs/reference.md`, referenceLabel: 'Method reference', examples: pythonExamples,
  },
} as const;
type SDKKind = keyof typeof sdkCatalog;

function CopyButton({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { setState('idle'); clearTimeout(timer.current); }, [text]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch { setState('error'); }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 3500);
  }
  return <button className="sdk-copy" data-copy-state={state} onClick={copy} type="button" aria-live="polite">
    {state === 'copied' ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
    {state === 'copied' ? 'Copied' : state === 'error' ? 'Select & copy manually' : 'Copy'}
  </button>;
}

function SourceCode({ code }: { code: string }) {
  return <code>{code.split(/('[^'\n]*'|"[^"\n]*"|\b(?:local|function|end|if|then|else|not|true|false|const|let|import|from|new|await|async|return|with|as|def|True|False|None|for|in)\b|\b\d+(?:\.\d+)?\b)/g).map((part, index) => {
    const type = /^['"]/.test(part) ? 'string' : /^(local|function|end|if|then|else|not|true|false|const|let|import|from|new|await|async|return|with|as|def|True|False|None|for|in)$/.test(part) ? 'keyword' : /^\d/.test(part) ? 'number' : undefined;
    return type ? <span className={`sdk-syntax-${type}`} key={index}>{part}</span> : part;
  })}</code>;
}

function SDKCard({ kind }: { kind: SDKKind }) {
  const [active, setActive] = useState(0);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const sdk = sdkCatalog[kind];
  const examples = sdk.examples;
  const installCommand = kind === 'python' ? pipInstall : kind === 'node' ? npmInstall : undefined;
  return <article className="sdk-resource" aria-labelledby={`${kind}-title`}>
      <div className="sdk-resource__overview">
        <div className="sdk-resource__identity">
          <svg className="sdk-lua" viewBox="0 0 24 24" role="img" aria-label={sdk.icon.title}><path d={sdk.icon.path} fill="currentColor" /></svg>
          <div><p className="sdk-resource__language">{sdk.language}</p><h2 id={`${kind}-title`}>{sdk.title}</h2></div>
        </div>
        <p className="sdk-resource__description">{sdk.description}</p>
        <ul className="sdk-capabilities">
          {sdk.capabilities.map(capability => <li key={capability}><Check size={16} aria-hidden="true" /> {capability}</li>)}
        </ul>
        <div className="sdk-release"><span>Beta</span><a href={kind === 'python' ? pythonPackage : `${sdk.repository}/releases/tag/v${sdk.version}`}>v{sdk.version} <ArrowUpRight size={13} aria-hidden="true" /></a><span>MIT license</span></div>
        {installCommand && <div className="sdk-package-command"><code>{installCommand}</code><CopyButton text={installCommand} /></div>}
        <a className="button sdk-download" href={sdk.download} aria-label={`Download ${sdk.title} SDK ${sdk.extension}`}><ArrowDownToLine size={18} aria-hidden="true" />Download SDK <span>{sdk.extension}</span></a>
        <Link className="sdk-guide-link" to={sdk.guide}>Installation guide <ArrowRight size={17} aria-hidden="true" /></Link>
        <div className="sdk-resource__links">
          <a href={sdk.repository}><Github size={16} aria-hidden="true" /> Source on GitHub</a>
          <a href={sdk.reference}>{sdk.referenceLabel} <ArrowUpRight size={15} aria-hidden="true" /></a>
        </div>
      </div>

      <div className="sdk-examples">
        <div className="sdk-tabs" role="tablist" aria-label={`${sdk.title} examples`}>
          {examples.map((item, index) => <button key={item.id} type="button" role="tab" id={`sdk-tab-${kind}-${item.id}`} aria-controls={`sdk-panel-${kind}-${item.id}`} aria-selected={active === index} tabIndex={active === index ? 0 : -1} ref={(node) => { tabs.current[index] = node; }} onClick={() => setActive(index)} onKeyDown={(event) => {
            let next = index;
            if (event.key === 'ArrowRight') next = (index + 1) % examples.length;
            else if (event.key === 'ArrowLeft') next = (index - 1 + examples.length) % examples.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = examples.length - 1;
            else return;
            event.preventDefault(); setActive(next); tabs.current[next]?.focus();
          }}>{item.label}</button>)}
        </div>
        <div className="sdk-example-panels">
        {examples.map((example, index) => <div key={example.id} className="sdk-example" role="tabpanel" id={`sdk-panel-${kind}-${example.id}`} aria-labelledby={`sdk-tab-${kind}-${example.id}`} aria-hidden={active !== index} inert={active !== index} tabIndex={active === index ? 0 : -1}>
          <div className="sdk-code-heading"><span>{example.filename}</span><CopyButton text={example.code} /></div>
          <pre className="sdk-code"><SourceCode code={example.code} /></pre>
          <div className="sdk-example__note"><p>{example.note}</p><a href={example.link}>{example.linkLabel} <ArrowUpRight size={14} aria-hidden="true" /></a></div>
        </div>)}
        </div>
      </div>
    </article>;
}

export function SDKs() {
  return <div className="sdk-page shell">
    <header className="sdk-intro">
      <p className="sdk-eyebrow">BCKT SDKs</p>
      <h1>Start with<br /><span>an export.</span></h1>
      <p>Official SDKs for your code. Upload files, deliver media and keep searchable logs without writing another HTTP wrapper.</p>
    </header>
    <div className="sdk-catalog"><SDKCard kind="cfx" /><SDKCard kind="node" /><SDKCard kind="python" /></div>
  </div>;
}

function GuideCode({ filename, code }: { filename: string; code: string }) {
  return <div className="sdk-install__code"><div className="sdk-code-heading"><span>{filename}</span><CopyButton text={code} /></div><pre><SourceCode code={code} /></pre></div>;
}

export function SDKGuide({ kind }: { kind: SDKKind }) {
  const sdk = sdkCatalog[kind];
  return <div className="sdk-page sdk-guide shell">
    <Link className="sdk-back" to="/sdks"><ArrowLeft size={16} aria-hidden="true" /> All SDKs</Link>
    <header className="sdk-intro">
      <p className="sdk-eyebrow">{sdk.language}</p>
      <h1>{sdk.title}<br /><span>Get started.</span></h1>
      <p>{kind === 'cfx' ? 'Add BCKT uploads and logs to your resources in a few steps.' : 'Install the package, connect your key and make your first upload.'}</p>
      <div className="sdk-guide-actions"><a className="button button--accent" href={sdk.download}><ArrowDownToLine size={17} aria-hidden="true" /> Download {sdk.extension}</a><a className="sdk-inline-link" href={sdk.reference}>{sdk.referenceLabel} <ArrowUpRight size={16} aria-hidden="true" /></a></div>
    </header>

    {kind === 'cfx' ? <>
    <section className="sdk-install" aria-labelledby="sdk-install-title">
      <div className="sdk-install__intro"><h2 id="sdk-install-title">Download. Add. Start.</h2><p>No build step on your game server.</p><p className="sdk-beta-note">This is a beta release. Test your integration on a development server before using it live.</p></div>
      <div className="sdk-install__steps">
        <div className="sdk-install__step"><h3>Add the resource</h3><p>Extract the ZIP into your server’s resources directory. Name the resource folder <code>bckt</code>.</p></div>
        <div className="sdk-install__step"><h3>Set your server key</h3><p><Link to="/dashboard/credentials">Create a BCKT API key</Link> with the permissions your resource needs. Add these lines to <code>server.cfg</code>, before the resources using the SDK.</p><div className="sdk-install__code"><div className="sdk-code-heading"><span>server.cfg</span><CopyButton text={installation} /></div><pre><code>{installation}</code></pre></div><p className="sdk-install__key-note">Use <code>set</code>, never <code>setr</code> or <code>sets</code>. Your API key stays on the server.</p></div>
        <div className="sdk-install__step"><h3>Connect your script</h3><p>Call the exports from your resource. For screenshots, start <code>screenshot-basic</code> or <code>screencapture</code> before <code>bckt</code>. Auto selection uses screenshot-basic first when both are running. Pass <code>provider = 'screencapture'</code> to choose it explicitly.</p><a className="sdk-inline-link" href={`${repository}/tree/main/examples`}>Open the integration examples <ArrowRight size={16} aria-hidden="true" /></a></div>
      </div>
    </section>
    </> : kind === 'python' ? <section className="sdk-install" aria-labelledby="sdk-install-title">
      <div className="sdk-install__intro"><h2 id="sdk-install-title">Install. Connect. Upload.</h2><p>Python 3.10 or newer. Sync or async.</p><p className="sdk-beta-note">Version {pythonVersion} is a public beta. Both clients include type hints and use HTTPX for connections.</p><a className="sdk-inline-link" href={pythonPackage}>View package on PyPI <ArrowUpRight size={15} aria-hidden="true" /></a></div>
      <div className="sdk-install__steps">
        <div className="sdk-install__step"><h3>Install the package</h3><p>Run this in your project's active virtual environment. The package is called <code>bckt-sdk</code>; import it as <code>bckt</code>.</p><GuideCode filename="Terminal" code={pipInstall} /></div>
        <div className="sdk-install__step"><h3>Connect your API key</h3><p><Link to="/dashboard/credentials">Create a BCKT API key</Link> and set it as the server-side environment variable <code>BCKT_API_KEY</code>. Uploads need <code>files:write</code>, browsing needs <code>files:read</code>, and logs use <code>logs:write</code> or <code>logs:read</code>.</p><GuideCode filename="app.py" code={`${pythonClient}

with Bckt(os.environ["BCKT_API_KEY"]) as bckt:
    files = bckt.files.list()
    print(files["pagination"]["total"])`} /><p>The context manager closes the connection pool. Keep your API key out of browser code and your repository.</p></div>
        <div className="sdk-install__step"><h3>Upload your first file</h3><p>Place a <code>photo.webp</code> alongside your app and run the example. The SDK streams the file from disk.</p><GuideCode filename="app.py" code={pythonExamples[0].code} /><p>Run <code>python app.py</code>. Set <code>private=True</code> for private files. Uploads return a <code>file_key</code>; management methods use the UUID from <code>files.list()</code>.</p></div>
        <div className="sdk-install__step"><h3>Connect your application</h3><p>Use <code>AsyncBckt</code> with <code>asyncio</code>, issue temporary upload URLs for the browser, or create a stream before sending logs. Buffered loggers send queued events when you call <code>flush()</code> or close them successfully.</p><a className="sdk-inline-link" href={`${pythonRepository}/tree/main/examples`}>Open the integration examples <ArrowRight size={16} aria-hidden="true" /></a><a className="sdk-inline-link sdk-guide-operations" href={`${pythonRepository}/blob/main/docs/operations.md`}>Retries, queues & error handling <ArrowUpRight size={16} aria-hidden="true" /></a></div>
      </div>
    </section> : <section className="sdk-install" aria-labelledby="sdk-install-title">
      <div className="sdk-install__intro"><h2 id="sdk-install-title">Install. Connect. Upload.</h2><p>Node.js 22 or newer. JavaScript or TypeScript.</p><p className="sdk-beta-note">Version {nodeVersion} is a public beta. The package supports ESM and CommonJS with no runtime dependencies.</p><a className="sdk-inline-link" href="https://www.npmjs.com/package/@bckt/sdk">View package on npm <ArrowUpRight size={15} aria-hidden="true" /></a></div>
      <div className="sdk-install__steps">
        <div className="sdk-install__step"><h3>Install the package</h3><p>Run this in your Node.js project to install the beta release.</p><GuideCode filename="Terminal" code={npmInstall} /></div>
        <div className="sdk-install__step"><h3>Connect your API key</h3><p><Link to="/dashboard/credentials">Create a BCKT API key</Link> and provide it as the server-side environment variable <code>BCKT_API_KEY</code>. Give it only the scopes your application needs.</p><GuideCode filename="app.mjs" code={nodeClient} /><p>For CommonJS, use <code>const {'{ Bckt }'} = require('@bckt/sdk')</code>. Keep your key out of browser code and public environment variables.</p></div>
        <div className="sdk-install__step"><h3>Upload your first file</h3><p>Place a <code>photo.webp</code> alongside your app, then add this after the client setup. The SDK streams the file from disk.</p><GuideCode filename="app.mjs" code={`const uploaded = await bckt.files.uploadFile('./photo.webp', {
  folder: 'photos',
  private: false,
});

console.log(uploaded.url);`} /><p>Set <code>private: true</code> for private files. Uploads return a <code>file_key</code>; management methods use the file UUID from <code>files.list()</code>.</p></div>
        <div className="sdk-install__step"><h3>Connect your application</h3><p>Use temporary upload URLs for browser uploads, create a stream before sending logs, and handle request errors in your application.</p><a className="sdk-inline-link" href={`${nodeRepository}/tree/main/examples`}>Open the integration examples <ArrowRight size={16} aria-hidden="true" /></a><a className="sdk-inline-link sdk-guide-operations" href={`${nodeRepository}/blob/main/docs/operations.md`}>Retries, queues & error handling <ArrowUpRight size={16} aria-hidden="true" /></a></div>
      </div>
    </section>}

    <section className="sdk-rest" aria-labelledby="sdk-rest-title"><div><h2 id="sdk-rest-title">Working in another language?</h2><p>The BCKT REST API is ready for your stack. Start with authentication, uploads and logs.</p></div><Link to="/docs#quickstart">Read the API docs <ArrowUpRight size={19} aria-hidden="true" /></Link></section>
  </div>;
}
